import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarRange } from "lucide-react";

const toISODate = (dt) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

export const localISO = () => toISODate(new Date());

const weekStartOf = (d) => {
  const dt = new Date(d + "T00:00:00");
  dt.setDate(dt.getDate() - dt.getDay()); // weeks start Sunday
  return toISODate(dt);
};

// Resolve a {start,end} range for a given anchor date + mode ("day" | "week" | "month" | "custom").
// Custom ranges are carried in `date` as "YYYY-MM-DD~YYYY-MM-DD".
export const rangeFor = (date, mode) => {
  if (mode === "custom" || String(date).includes("~")) {
    const [s, e] = String(date).split("~");
    return { start: s, end: e || s };
  }
  if (mode === "week") {
    const s = weekStartOf(date);
    const e = new Date(s + "T00:00:00");
    e.setDate(e.getDate() + 6);
    return { start: s, end: toISODate(e) };
  }
  if (mode === "month") {
    const dt = new Date(date + "T00:00:00");
    return { start: toISODate(new Date(dt.getFullYear(), dt.getMonth(), 1)), end: toISODate(new Date(dt.getFullYear(), dt.getMonth() + 1, 0)) };
  }
  return { start: date, end: date };
};

const longDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const shortDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

export const ActiveDot = ({ show }) => (show
  ? <span title="Clocked in now" data-testid="active-dot" className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-200 animate-pulse shrink-0" />
  : null);

export function PeriodBar({ date, mode, setDate, setMode, testid = "period", modes = ["day", "week", "month"] }) {
  const anchor = String(date).split("~")[0];
  const { start, end } = rangeFor(date, mode);
  const [from, setFrom] = useState(start);
  const [to, setTo] = useState(end);
  const [open, setOpen] = useState(false);
  const step = (dir) => {
    if (mode === "custom") {
      const len = Math.round((new Date(end + "T00:00:00") - new Date(start + "T00:00:00")) / 864e5) + 1;
      const s = new Date(start + "T00:00:00"), e = new Date(end + "T00:00:00");
      s.setDate(s.getDate() + dir * len); e.setDate(e.getDate() + dir * len);
      setDate(`${toISODate(s)}~${toISODate(e)}`);
      return;
    }
    const dt = new Date(anchor + "T00:00:00");
    if (mode === "month") dt.setMonth(dt.getMonth() + dir, 1);
    else dt.setDate(dt.getDate() + dir * (mode === "week" ? 7 : 1));
    setDate(toISODate(dt));
  };
  const pickMode = (m) => { if (mode === "custom") setDate(anchor); setMode(m); };
  const month = (offset) => {
    const n = new Date();
    setDate(toISODate(new Date(n.getFullYear(), n.getMonth() + offset, 1)));
    setMode("month");
  };
  const thisMonth = toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const lastMonth = toISODate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));
  const applyRange = () => {
    if (!from || !to) return;
    const [s, e] = from <= to ? [from, to] : [to, from];
    setDate(`${s}~${e}`);
    setMode("custom");
    setOpen(false);
  };
  const label = mode === "custom"
    ? `${shortDate(start)}, ${new Date(start + "T00:00:00").getFullYear()} – ${shortDate(end)}, ${new Date(end + "T00:00:00").getFullYear()}`
    : mode === "week"
      ? `${shortDate(start)} – ${shortDate(end)}, ${new Date(end + "T00:00:00").getFullYear()}`
      : mode === "month" ? new Date(start + "T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" })
        : longDate(date);
  const chip = (on) => `h-9 ${on ? "border-brand bg-brand/10 text-brand font-semibold" : ""}`;
  return (
    <div className="space-y-2" data-testid={`${testid}-bar`}>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid={`${testid}-prev`} onClick={() => step(-1)}><ChevronLeft className="h-5 w-5" /></Button>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`${testid}-label`}
              className="flex-1 h-10 flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white font-semibold text-slate-800 text-sm sm:text-base hover:bg-slate-50 transition">
              <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" />{label}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center" data-testid={`${testid}-calendar`}>
            <Calendar mode="single" weekStartsOn={0} defaultMonth={new Date(anchor + "T00:00:00")} selected={new Date(anchor + "T00:00:00")}
              onSelect={(d) => { if (d) { setDate(toISODate(d)); if (mode === "custom") setMode("day"); } }} initialFocus />
          </PopoverContent>
        </Popover>
        <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid={`${testid}-next`} onClick={() => step(1)}><ChevronRight className="h-5 w-5" /></Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-9" data-testid={`${testid}-today`} onClick={() => { setDate(localISO()); if (mode === "custom") setMode("day"); }}>Today</Button>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={`${testid}-toggle`}>
          {modes.map((m) => (
            <button key={m} type="button" data-testid={`${testid}-${m}`} onClick={() => pickMode(m)}
              className={`px-5 h-9 text-sm font-semibold capitalize transition ${mode === m ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{m}</button>
          ))}
        </div>
        <Button variant="outline" size="sm" className={chip(mode === "month" && anchor === thisMonth)} data-testid={`${testid}-this-month`} onClick={() => month(0)}>This month</Button>
        <Button variant="outline" size="sm" className={chip(mode === "month" && anchor === lastMonth)} data-testid={`${testid}-last-month`} onClick={() => month(-1)}>Last month</Button>
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setFrom(start); setTo(end); } }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={chip(mode === "custom")} data-testid={`${testid}-custom`}><CalendarRange className="h-4 w-4 mr-1.5" />Date range</Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 space-y-3" align="start" data-testid={`${testid}-range-popover`}>
            <div className="text-sm font-semibold">Show times between</div>
            <label className="block text-xs text-slate-500">From
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testid}-from`}
                className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm text-slate-800" /></label>
            <label className="block text-xs text-slate-500">To
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testid}-to`}
                className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm text-slate-800" /></label>
            <Button className="w-full" disabled={!from || !to} onClick={applyRange} data-testid={`${testid}-range-apply`}>Show</Button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
